import React, { useState, useEffect, useCallback } from 'react';
import { useModal } from '../../components/modal/ModalContext';
import AlertModal from '../../components/modal/AlertModal';
import ConfirmModal from '../../components/modal/ConfirmModal';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

type Tab = 'plans' | 'subscriptions' | 'settings';

export default function BillingAdmin() {
  const { openModal, closeModal } = useModal();
  const [tab, setTab] = useState<Tab>('plans');
  const [plans, setPlans] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [planForm, setPlanForm] = useState<any>({
    name: '', description: '', tier: 'starter', price: 0, currency: 'GBP', billing_cycle: 'monthly',
    max_companies: 1, max_users: 10, max_templates: 5, max_frameworks: 3,
    ai_assistant: false, gap_analysis: false, framework_mapping: false, evidence_versioning: false,
    global_dashboard: false, api_access: false, priority_support: false, custom_branding: false,
    sso_integration: false, audit_logs_retention: 90,
  });

  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetch('/api/billing/plans');
      const d = await res.json();
      if (d.success) setPlans(d.plans || []);
    } catch {}
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/billing/status', { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setStatus(d);
    } catch {}
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch('/api/settings/system', { headers: authHeaders() });
      const d = await res.json();
      if (d.success && d.settings) {
        const map: Record<string, string> = {};
        const s = d.settings;
        // Extract billing-related settings
        const billingKeys = ['stripe_secret_key', 'stripe_publishable_key', 'stripe_webhook_secret', 'billing_currency', 'billing_vat_rate', 'billing_enabled'];
        for (const k of billingKeys) {
          if (s[k] !== undefined) map[k] = String(s[k]);
        }
        setSettings(prev => ({ ...prev, ...map }));
      }
    } catch {}
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const res = await fetch('/api/subscription-management/plans', { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setSubscriptions(d.plans || []);
    } catch {}
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchPlans(), fetchStatus(), fetchSettings(), fetchSubscriptions()]).finally(() => setLoading(false));
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await fetch('/api/settings/system', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(settings),
      });
      await fetchStatus();
      openModal({ component: AlertModal, props: { title: 'Settings Saved', message: 'Billing settings have been saved successfully.', type: 'success' } });
    } catch {
      openModal({ component: AlertModal, props: { title: 'Save Failed', message: 'Failed to save billing settings. Please try again.', type: 'danger' } });
    } finally {
      setSaving(false);
    }
  };

  const handleSyncPlans = async () => {
    try {
      const res = await fetch('/api/billing/sync-plans', { method: 'POST', headers: authHeaders() });
      const d = await res.json();
      if (d.success) {
        openModal({ component: AlertModal, props: { title: 'Plans Synced', message: `Synced ${d.synced} plans to Stripe.${d.errors?.length ? ` Errors: ${d.errors.join(', ')}` : ''}`, type: d.errors?.length ? 'warning' : 'success' } });
        fetchPlans();
      } else {
        openModal({ component: AlertModal, props: { title: 'Sync Failed', message: d.message || 'Failed to sync plans to Stripe.', type: 'danger' } });
      }
    } catch {
      openModal({ component: AlertModal, props: { title: 'Sync Failed', message: 'Failed to connect to Stripe. Check your API keys.', type: 'danger' } });
    }
  };

  const maskKey = (key: string) => {
    if (!key || key.length < 12) return key ? '****' : '';
    return key.slice(0, 7) + '****' + key.slice(-4);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Billing & Subscriptions</h1>
        <p className="text-sm text-slate-500 mt-1">Manage Stripe integration, subscription plans, and billing settings</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200 dark:border-slate-700">
        {([['settings', 'Stripe Settings'], ['plans', 'Subscription Plans'], ['subscriptions', 'Active Subscriptions']] as [Tab, string][]).map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
              tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>{label}</button>
        ))}
      </div>

      {/* Status Bar */}
      {status && (
        <div className={`mb-6 p-4 rounded-xl border ${status.stripe_configured ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'}`}>
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${status.stripe_configured ? 'bg-green-500' : 'bg-amber-500'}`} />
            <span className={`text-sm font-semibold ${status.stripe_configured ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
              {status.stripe_configured ? 'Stripe Connected' : 'Stripe Not Configured'}
            </span>
          </div>
        </div>
      )}

      {/* Settings Tab — 2 column layout: form left, guide right */}
      {tab === 'settings' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="font-bold text-slate-900 dark:text-white mb-4">Stripe API Configuration</h3>
          <p className="text-xs text-slate-500 mb-6">Enter your Stripe API keys from the Stripe Dashboard. Keys are stored securely in the system database.</p>

          <div className="space-y-4 max-w-xl">
            {[
              { key: 'stripe_secret_key', label: 'Secret Key', placeholder: 'sk_live_...', sensitive: true },
              { key: 'stripe_publishable_key', label: 'Publishable Key', placeholder: 'pk_live_...' },
              { key: 'stripe_webhook_secret', label: 'Webhook Secret', placeholder: 'whsec_...', sensitive: true },
              { key: 'billing_currency', label: 'Currency', placeholder: 'GBP' },
              { key: 'billing_vat_rate', label: 'VAT Rate (%)', placeholder: '20' },
            ].map(field => (
              <div key={field.key}>
                <label className="block text-xs font-semibold text-slate-500 mb-1">{field.label}</label>
                <input
                  type={field.sensitive ? 'password' : 'text'}
                  value={settings[field.key] || ''}
                  onChange={e => setSettings({ ...settings, [field.key]: e.target.value })}
                  placeholder={field.placeholder}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                />
                {field.sensitive && settings[field.key] && (
                  <p className="text-[10px] text-slate-400 mt-1">Current: {maskKey(settings[field.key] || '')}</p>
                )}
              </div>
            ))}

            <div className="flex items-center gap-3">
              <label className="text-xs font-semibold text-slate-500">Enable Billing</label>
              <input
                type="checkbox"
                checked={settings['billing_enabled'] === 'true' || settings['billing_enabled'] === '1'}
                onChange={e => setSettings({ ...settings, billing_enabled: e.target.checked ? 'true' : 'false' })}
                className="rounded border-slate-300"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <button onClick={handleSaveSettings} disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
              <button onClick={handleSyncPlans}
                className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300">
                Sync Plans to Stripe
              </button>
            </div>
          </div>
        </div>

        {/* Right column: Setup Guide */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 h-fit sticky top-6">
          <h3 className="font-bold text-blue-900 dark:text-blue-300 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
            Setup Guide
          </h3>
          <div className="space-y-3">
            {[
              { step: 1, title: 'Create Stripe Account', desc: <>Go to <a href="https://dashboard.stripe.com/register" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-blue-800">dashboard.stripe.com</a> and sign up.</>, done: false },
              { step: 2, title: 'Get API Keys', desc: <>Go to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-blue-800">Developers &gt; API Keys</a> and copy your keys.</>, done: false },
              { step: 3, title: 'Enter Keys', desc: 'Paste Secret Key and Publishable Key in the form.', done: !!settings['stripe_secret_key'] },
              { step: 4, title: 'Add Webhook', desc: <>Go to <a href="https://dashboard.stripe.com/webhooks/create" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-blue-800">Webhooks &gt; Add endpoint</a>. URL:<br/><code className="bg-blue-100 dark:bg-blue-800 px-1.5 py-0.5 rounded text-[10px] select-all inline-block mt-1">https://eis-tech.co.uk/api/billing/webhooks</code></>, done: false },
              { step: 5, title: 'Select Events', desc: <><code className="text-[9px] bg-blue-100 dark:bg-blue-800 px-1 rounded">customer.subscription.*</code> <code className="text-[9px] bg-blue-100 dark:bg-blue-800 px-1 rounded">invoice.paid</code> <code className="text-[9px] bg-blue-100 dark:bg-blue-800 px-1 rounded">invoice.payment_failed</code></>, done: false },
              { step: 6, title: 'Webhook Secret', desc: <>Copy the Signing Secret (<code className="text-[10px] bg-blue-100 dark:bg-blue-800 px-1 rounded">whsec_...</code>) and paste above.</>, done: !!settings['stripe_webhook_secret'] },
              { step: 7, title: 'Enable & Save', desc: 'Toggle "Enable Billing" and click Save Settings.', done: settings['billing_enabled'] === 'true' },
              { step: 8, title: 'Sync Plans', desc: <>Go to <button onClick={() => setTab('plans')} className="underline font-semibold hover:text-blue-800">Subscription Plans</button> tab and click "Sync Plans to Stripe".</>, done: false },
            ].map(s => (
              <div key={s.step} className="flex gap-2.5">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 ${
                  s.done ? 'bg-green-500 text-white' : 'bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300'
                }`}>{s.done ? '\u2713' : s.step}</div>
                <div>
                  <div className="text-xs font-semibold text-blue-900 dark:text-blue-200">{s.title}</div>
                  <div className="text-[11px] text-blue-600 dark:text-blue-400 leading-relaxed">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-[10px] text-amber-700 dark:text-amber-400">
              <strong>Use test keys</strong> (<code className="bg-amber-100 dark:bg-amber-800 px-0.5 rounded">sk_test_</code>) for development. SSL required for live webhooks.
            </p>
          </div>
        </div>
        </div>
      )}

      {/* Plans Tab */}
      {tab === 'plans' && (
        <div>
          {/* Plan Form (Create / Edit) */}
          {showPlanForm && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-6">
              <h3 className="font-bold text-slate-900 dark:text-white mb-4">{editingPlan ? 'Edit Plan' : 'Create Plan'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Name *</label>
                  <input value={planForm.name} onChange={e => setPlanForm({ ...planForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Tier *</label>
                  <select value={planForm.tier} onChange={e => setPlanForm({ ...planForm, tier: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
                    {['free', 'starter', 'professional', 'enterprise', 'custom'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Billing Cycle</label>
                  <select value={planForm.billing_cycle} onChange={e => setPlanForm({ ...planForm, billing_cycle: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
                    {['monthly', 'quarterly', 'annual'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Full Value (GBP) — retail price before discount</label>
                  <input type="number" step="0.01" value={planForm.original_price || planForm.price} onChange={e => setPlanForm({ ...planForm, original_price: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Discount %</label>
                  <input type="number" min="0" max="100" value={planForm.discount_pct || 0} onChange={e => setPlanForm({ ...planForm, discount_pct: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Customer Pays (GBP) — price after discount</label>
                  <input type="number" step="0.01" value={planForm.price} onChange={e => setPlanForm({ ...planForm, price: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Discount Label</label>
                  <input value={planForm.discount_label || ''} onChange={e => setPlanForm({ ...planForm, discount_label: e.target.value })} placeholder="e.g. Early Adopter Discount"
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Discount Valid Until</label>
                  <input type="date" value={planForm.discount_valid_until || ''} onChange={e => setPlanForm({ ...planForm, discount_valid_until: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Max Users (-1 = unlimited)</label>
                  <input type="number" value={planForm.max_users} onChange={e => setPlanForm({ ...planForm, max_users: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Max Frameworks (-1 = unlimited)</label>
                  <input type="number" value={planForm.max_frameworks} onChange={e => setPlanForm({ ...planForm, max_frameworks: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Max Templates (-1 = unlimited)</label>
                  <input type="number" value={planForm.max_templates} onChange={e => setPlanForm({ ...planForm, max_templates: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Max Companies (-1 = unlimited)</label>
                  <input type="number" value={planForm.max_companies} onChange={e => setPlanForm({ ...planForm, max_companies: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Audit Log Retention (days)</label>
                  <input type="number" value={planForm.audit_logs_retention} onChange={e => setPlanForm({ ...planForm, audit_logs_retention: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
                  <textarea value={planForm.description} onChange={e => setPlanForm({ ...planForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white min-h-[60px]" />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-slate-500 mb-2">Features</label>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { key: 'ai_assistant', label: 'AI Assistant' },
                      { key: 'gap_analysis', label: 'Gap Analysis' },
                      { key: 'framework_mapping', label: 'Framework Mapping' },
                      { key: 'evidence_versioning', label: 'Evidence Versioning' },
                      { key: 'global_dashboard', label: 'Global Dashboard' },
                      { key: 'api_access', label: 'API Access' },
                      { key: 'priority_support', label: 'Priority Support' },
                      { key: 'custom_branding', label: 'Custom Branding' },
                      { key: 'sso_integration', label: 'SSO Integration' },
                    ].map(f => (
                      <label key={f.key} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                        <input type="checkbox" checked={!!planForm[f.key]}
                          onChange={e => setPlanForm({ ...planForm, [f.key]: e.target.checked })}
                          className="rounded border-slate-300" />
                        {f.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={async () => {
                  try {
                    const method = editingPlan ? 'PUT' : 'POST';
                    const url = editingPlan ? `/api/billing/plans/${editingPlan.id}` : '/api/billing/plans';
                    const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(planForm) });
                    const d = await res.json();
                    if (d.success) { setShowPlanForm(false); setEditingPlan(null); fetchPlans(); }
                    else openModal({ component: AlertModal, props: { title: 'Save Failed', message: d.message || 'Failed to save plan.', type: 'danger' } });
                  } catch { openModal({ component: AlertModal, props: { title: 'Save Failed', message: 'Failed to save plan.', type: 'danger' } }); }
                }} className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700">
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
                <button onClick={() => { setShowPlanForm(false); setEditingPlan(null); }}
                  className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
              <span className="text-xs font-bold text-slate-400 uppercase">{plans.length} Plans</span>
              <button onClick={() => {
                setEditingPlan(null);
                setPlanForm({ name: '', description: '', tier: 'starter', price: 0, currency: 'GBP', billing_cycle: 'monthly',
                  max_companies: 1, max_users: 10, max_templates: 5, max_frameworks: 3,
                  ai_assistant: false, gap_analysis: false, framework_mapping: false, evidence_versioning: false,
                  global_dashboard: false, api_access: false, priority_support: false, custom_branding: false,
                  sso_integration: false, audit_logs_retention: 90 });
                setShowPlanForm(true);
              }} className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Add Plan
              </button>
            </div>
            {plans.length === 0 ? (
              <div className="p-12 text-sm text-slate-400">No plans configured</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Tier</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Price</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Billing</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Users</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Features</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {plans.map((p: any) => (
                      <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                        <td className="px-4 py-3 text-left">
                          <div className="font-semibold text-slate-900 dark:text-white">{p.name}</div>
                          <div className="text-[10px] text-slate-400 font-mono">{p.id}</div>
                        </td>
                        <td className="px-4 py-3 text-left">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            p.tier === 'free' ? 'bg-slate-100 text-slate-600' :
                            p.tier === 'starter' ? 'bg-blue-100 text-blue-700' :
                            p.tier === 'professional' ? 'bg-purple-100 text-purple-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>{p.tier}</span>
                        </td>
                        <td className="px-4 py-3 text-left">
                          {p.original_price && p.original_price > p.price ? (
                            <div>
                              <span className="line-through text-slate-400 text-xs">{p.currency} {Number(p.original_price).toLocaleString()}</span>
                              {p.discount_pct > 0 && <span className="ml-1 text-xs font-medium text-green-600 dark:text-green-400">-{p.discount_pct}%</span>}
                              <div className="font-semibold text-slate-900 dark:text-white">{p.currency} {Number(p.price).toLocaleString()}</div>
                              {p.discount_label && <div className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">{p.discount_label}</div>}
                            </div>
                          ) : (
                            <span className="font-semibold text-slate-900 dark:text-white">{p.currency} {Number(p.price).toLocaleString()}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-left text-xs text-slate-500">{p.billing_cycle}</td>
                        <td className="px-4 py-3 text-left text-xs text-slate-500">{p.max_users < 0 ? '∞' : p.max_users}</td>
                        <td className="px-4 py-3 text-left">
                          <div className="flex flex-wrap gap-1">
                            {p.ai_assistant ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700">AI</span> : null}
                            {p.gap_analysis ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Gap</span> : null}
                            {p.framework_mapping ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Map</span> : null}
                            {p.api_access ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700">API</span> : null}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-left">
                          <div className="flex gap-1">
                            <button onClick={() => {
                              setEditingPlan(p);
                              setPlanForm({
                                name: p.name, description: p.description || '', tier: p.tier, price: p.price,
                                original_price: p.original_price || p.price,
                                discount_pct: p.discount_pct || 0,
                                discount_label: p.discount_label || '',
                                discount_valid_until: p.discount_valid_until ? p.discount_valid_until.split('T')[0] : '',
                                currency: p.currency || 'GBP', billing_cycle: p.billing_cycle || 'monthly',
                                max_companies: p.max_companies, max_users: p.max_users,
                                max_templates: p.max_templates, max_frameworks: p.max_frameworks,
                                ai_assistant: !!p.ai_assistant, gap_analysis: !!p.gap_analysis,
                                framework_mapping: !!p.framework_mapping, evidence_versioning: !!p.evidence_versioning,
                                global_dashboard: !!p.global_dashboard, api_access: !!p.api_access,
                                priority_support: !!p.priority_support, custom_branding: !!p.custom_branding,
                                sso_integration: !!p.sso_integration, audit_logs_retention: p.audit_logs_retention || 30,
                              });
                              setShowPlanForm(true);
                            }} className="text-[10px] font-bold text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-2 py-1 rounded">Edit</button>
                            <button onClick={() => {
                              const modalId = openModal({
                                component: ConfirmModal,
                                props: {
                                  title: 'Deactivate Plan',
                                  message: `Are you sure you want to deactivate "${p.name}"? Companies on this plan will not be affected, but new subscriptions cannot use it.`,
                                  confirmText: 'Deactivate',
                                  type: 'warning',
                                  onConfirm: async () => {
                                    try {
                                      await fetch(`/api/billing/plans/${p.id}`, { method: 'DELETE', headers: authHeaders() });
                                      fetchPlans();
                                    } catch {}
                                    closeModal(modalId);
                                  },
                                  onCancel: () => closeModal(modalId),
                                }
                              });
                            }} className="text-[10px] font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded">Deactivate</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Subscriptions Tab */}
      {tab === 'subscriptions' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="font-bold text-slate-900 dark:text-white mb-4">Active Subscriptions</h3>
          <p className="text-xs text-slate-500 mb-4">Company subscriptions are managed through the existing Subscription Management service. Stripe handles payment processing.</p>
          <div className="text-sm text-slate-400">
            Use the Subscription Management API to view and manage company subscriptions.
          </div>
        </div>
      )}
    </div>
  );
}
